import { useCallback, useEffect, useRef, useState } from 'react'
import { VolumeIcon, VolumeXIcon } from '@/components/Icons'
import { useUiStrings } from '@/hooks/useUiStrings'

const PLAYBACK_SPEEDS = [1, 1.25, 1.5, 2] as const
const SKIP_SECONDS = 10

const WAVEFORM_HEIGHTS = [
  24, 18, 32, 28, 14, 22, 36, 30, 16, 26, 34, 20, 12, 28, 38, 24, 18, 32, 22, 16, 30, 26, 14, 34,
  20, 28, 36, 18, 24, 32, 16, 22, 30, 26, 12, 28, 34, 20, 18, 24, 32, 16, 26, 30, 22, 14, 28, 36,
]

function formatAudioTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '00:00'
  const total = Math.floor(seconds)
  const minutes = Math.floor(total / 60)
  const secs = total % 60
  return `${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
}

function readMediaDuration(audio: HTMLAudioElement): number {
  if (Number.isFinite(audio.duration) && audio.duration > 0) {
    return audio.duration
  }

  if (audio.seekable.length > 0) {
    const end = audio.seekable.end(audio.seekable.length - 1)
    if (Number.isFinite(end) && end > 0) return end
  }

  return 0
}

/**
 * Playback length for the scrubber / total label.
 * Prefer the media element's own duration when known; fall back to the meeting's
 * saved wall-clock duration only while WebM metadata is still missing (Infinity),
 * then to the highest currentTime observed so far.
 */
function resolveDuration(
  savedDurationSeconds: number,
  mediaDuration: number,
  observedDuration: number
): number {
  if (mediaDuration > 0) return mediaDuration
  if (savedDurationSeconds > 0) return savedDurationSeconds
  if (observedDuration > 0) return observedDuration
  return 0
}

interface MeetingAudioPlayerProps {
  src: string
  durationSeconds?: number
}

export default function MeetingAudioPlayer({ src, durationSeconds = 0 }: MeetingAudioPlayerProps) {
  const { t } = useUiStrings()
  const audioRef = useRef<HTMLAudioElement>(null)
  const trackRef = useRef<HTMLDivElement>(null)
  const isScrubbingRef = useRef(false)
  const [trackWidth, setTrackWidth] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [mediaDuration, setMediaDuration] = useState(0)
  const [observedDuration, setObservedDuration] = useState(0)
  const [playbackSpeedIndex, setPlaybackSpeedIndex] = useState(0)
  const [isMuted, setIsMuted] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)

  const effectiveDuration = resolveDuration(durationSeconds, mediaDuration, observedDuration)
  const progress =
    effectiveDuration > 0 ? Math.min(1, Math.max(0, currentTime / effectiveDuration)) : 0
  const playbackSpeed = PLAYBACK_SPEEDS[playbackSpeedIndex]
  const seekMax = Math.max(effectiveDuration, 1)

  const syncMediaDuration = useCallback(() => {
    const audio = audioRef.current
    if (!audio) return
    const next = readMediaDuration(audio)
    if (next > 0) setMediaDuration(next)
  }, [])

  useEffect(() => {
    const track = trackRef.current
    if (!track) return

    const updateWidth = () => setTrackWidth(track.clientWidth)
    updateWidth()

    const observer = new ResizeObserver(updateWidth)
    observer.observe(track)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    setCurrentTime(0)
    setMediaDuration(0)
    setObservedDuration(0)
    setIsPlaying(false)
    setIsLoading(true)
    setHasError(false)
  }, [src])

  const syncPlaybackClock = useCallback(() => {
    const audio = audioRef.current
    if (!audio || isScrubbingRef.current) return
    const time = audio.currentTime
    setCurrentTime(time)
    if (time > 0) {
      setObservedDuration((prev) => Math.max(prev, time))
    }
  }, [])

  // While playing, sample audio.currentTime every frame so the counter stays
  // aligned with the media clock (timeupdate alone fires only ~4×/s).
  useEffect(() => {
    if (!isPlaying) return

    let rafId = 0
    const tick = () => {
      syncPlaybackClock()
      syncMediaDuration()
      rafId = requestAnimationFrame(tick)
    }
    rafId = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafId)
  }, [isPlaying, syncMediaDuration, syncPlaybackClock])

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const handleTimeUpdate = () => {
      syncPlaybackClock()
      syncMediaDuration()
    }

    const handlePlay = () => setIsPlaying(true)
    const handlePause = () => setIsPlaying(false)
    const handleEnded = () => {
      setIsPlaying(false)
      const endedAt = audio.currentTime
      const mediaTotal = readMediaDuration(audio)
      if (mediaTotal > 0) setMediaDuration(mediaTotal)
      // Always pin the counter to the media clock — never jump to saved wall-clock.
      setCurrentTime(endedAt)
      if (endedAt > 0) {
        setObservedDuration((prev) => Math.max(prev, endedAt, mediaTotal))
      }
    }

    const handleDurationSync = () => syncMediaDuration()
    const handleReady = () => {
      setIsLoading(false)
      syncMediaDuration()
    }
    const handleError = () => {
      setHasError(true)
      setIsLoading(false)
    }

    audio.addEventListener('loadedmetadata', handleReady)
    audio.addEventListener('durationchange', handleDurationSync)
    audio.addEventListener('loadeddata', handleReady)
    audio.addEventListener('canplay', handleReady)
    audio.addEventListener('progress', handleDurationSync)
    audio.addEventListener('timeupdate', handleTimeUpdate)
    audio.addEventListener('play', handlePlay)
    audio.addEventListener('pause', handlePause)
    audio.addEventListener('ended', handleEnded)
    audio.addEventListener('error', handleError)

    syncMediaDuration()

    return () => {
      audio.removeEventListener('loadedmetadata', handleReady)
      audio.removeEventListener('durationchange', handleDurationSync)
      audio.removeEventListener('loadeddata', handleReady)
      audio.removeEventListener('canplay', handleReady)
      audio.removeEventListener('progress', handleDurationSync)
      audio.removeEventListener('timeupdate', handleTimeUpdate)
      audio.removeEventListener('play', handlePlay)
      audio.removeEventListener('pause', handlePause)
      audio.removeEventListener('ended', handleEnded)
      audio.removeEventListener('error', handleError)
    }
  }, [src, syncMediaDuration, syncPlaybackClock])

  const seekTo = useCallback(
    (nextTime: number) => {
      const totalDuration = resolveDuration(durationSeconds, mediaDuration, observedDuration)
      const audio = audioRef.current
      if (!audio) return

      const upperBound =
        totalDuration > 0 ? totalDuration : Math.max(observedDuration, audio.currentTime, 1)
      const clamped = Math.min(upperBound, Math.max(0, nextTime))
      audio.currentTime = clamped
      setCurrentTime(clamped)
    },
    [durationSeconds, mediaDuration, observedDuration]
  )

  const togglePlay = useCallback(async () => {
    const audio = audioRef.current
    if (!audio || hasError) return

    if (audio.paused) {
      try {
        await audio.play()
      } catch {
        /* autoplay restrictions */
      }
      return
    }

    audio.pause()
  }, [hasError])

  const handleSeekInput = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      seekTo(Number(event.target.value))
    },
    [seekTo]
  )

  const skipBy = useCallback(
    (delta: number) => {
      seekTo(currentTime + delta)
    },
    [currentTime, seekTo]
  )

  const cyclePlaybackSpeed = useCallback(() => {
    const audio = audioRef.current
    const nextIndex = (playbackSpeedIndex + 1) % PLAYBACK_SPEEDS.length
    const nextSpeed = PLAYBACK_SPEEDS[nextIndex]
    setPlaybackSpeedIndex(nextIndex)
    if (audio) audio.playbackRate = nextSpeed
  }, [playbackSpeedIndex])

  const toggleMute = useCallback(() => {
    const audio = audioRef.current
    if (!audio) return
    const nextMuted = !audio.muted
    audio.muted = nextMuted
    setIsMuted(nextMuted)
  }, [])

  const showBufferingState = isLoading && durationSeconds <= 0

  return (
    <section className="overflow-hidden rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-[var(--border-color)] dark:bg-[var(--bg-secondary)]">
      <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400 dark:text-[var(--text-tertiary)]">
        {t.audioPlayback}
      </p>

      {hasError ? (
        <p role="alert" className="mt-4 text-sm font-medium text-red-600">
          {t.audioPlaybackError}
        </p>
      ) : (
        <>
          {showBufferingState && (
            <div className="mt-4 flex items-center gap-2 text-xs font-medium text-slate-400 dark:text-[var(--text-tertiary)]">
              <span
                className="h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-slate-200 border-t-blue-500 dark:border-[var(--border-color)] dark:border-t-[var(--accent)]"
                aria-hidden="true"
              />
              <span>{t.loading}</span>
            </div>
          )}

          <div className={showBufferingState ? 'mt-2' : 'mt-4'}>
            <div ref={trackRef} className="relative h-14 w-full">
              <div className="pointer-events-none absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-slate-200 dark:bg-[var(--bg-tertiary)]" />

              <div className="pointer-events-none absolute inset-0 flex items-center gap-[3px]">
                <WaveformBars active={false} />
              </div>

              <div
                className="pointer-events-none absolute inset-y-0 left-0 overflow-hidden"
                style={{ width: `${progress * 100}%` }}
              >
                <div
                  className="flex h-full items-center gap-[3px]"
                  style={{ width: trackWidth || '100%' }}
                >
                  <WaveformBars active />
                </div>
              </div>

              <div
                className="pointer-events-none absolute top-1/2 z-10 -translate-x-1/2 -translate-y-1/2"
                style={{ left: `${progress * 100}%` }}
              >
                <span className="block h-3.5 w-3.5 rounded-full border-2 border-blue-500 bg-white shadow-md ring-2 ring-white dark:border-[var(--accent)] dark:bg-[var(--bg-secondary)] dark:ring-[var(--bg-secondary)]" />
              </div>

              <input
                type="range"
                min={0}
                max={seekMax}
                step={0.1}
                value={Math.min(currentTime, seekMax)}
                onChange={handleSeekInput}
                onInput={handleSeekInput}
                onPointerDown={() => {
                  isScrubbingRef.current = true
                }}
                onPointerUp={() => {
                  isScrubbingRef.current = false
                }}
                onPointerCancel={() => {
                  isScrubbingRef.current = false
                }}
                onBlur={() => {
                  isScrubbingRef.current = false
                }}
                aria-label={t.audioSeekAria}
                aria-valuemin={0}
                aria-valuemax={Math.round(effectiveDuration > 0 ? effectiveDuration : seekMax)}
                aria-valuenow={Math.round(currentTime)}
                className="audio-seek-range absolute inset-0 z-20 m-0 h-full w-full cursor-pointer appearance-none bg-transparent"
              />
            </div>

            <div className="mt-2 flex items-center justify-between text-sm font-medium tabular-nums text-slate-500 dark:text-[var(--text-tertiary)]">
              <span>{formatAudioTime(currentTime)}</span>
              <span>{formatAudioTime(effectiveDuration)}</span>
            </div>
          </div>
        </>
      )}

      <div className="mt-5 grid grid-cols-[1fr_auto] items-center gap-3">
        <div className="flex items-center justify-center gap-2">
          <IconButton
            label={t.skipBackAria}
            onClick={() => skipBy(-SKIP_SECONDS)}
            disabled={hasError}
          >
            <SkipBackIcon className="h-5 w-5" />
          </IconButton>

          <button
            type="button"
            onClick={() => void togglePlay()}
            disabled={hasError}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-500 text-white shadow-lg shadow-blue-500/25 transition-colors hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-[var(--accent)] dark:shadow-none dark:hover:bg-[var(--accent-hover)]"
            aria-label={isPlaying ? t.pause : t.start}
          >
            {isPlaying ? <PauseIcon className="h-4 w-4" /> : <PlayIcon className="h-4 w-4" />}
          </button>

          <IconButton
            label={t.skipForwardAria}
            onClick={() => skipBy(SKIP_SECONDS)}
            disabled={hasError}
          >
            <SkipForwardIcon className="h-5 w-5" />
          </IconButton>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={cyclePlaybackSpeed}
            disabled={hasError}
            className="min-w-9 text-center text-sm font-semibold tabular-nums text-slate-800 transition-colors hover:text-slate-950 disabled:cursor-not-allowed disabled:opacity-50 dark:text-[var(--text-secondary)] dark:hover:text-[var(--text-primary)]"
            aria-label={t.playbackSpeedAria}
          >
            {playbackSpeed === 1 ? '1x' : `${playbackSpeed}x`}
          </button>

          <IconButton label={isMuted ? t.unmute : t.mute} onClick={toggleMute} disabled={hasError}>
            {isMuted ? <VolumeXIcon className="h-5 w-5" /> : <VolumeIcon className="h-5 w-5" />}
          </IconButton>
        </div>
      </div>

      <audio ref={audioRef} src={src} preload="auto" className="hidden">
        {t.audioUnsupported}
      </audio>
    </section>
  )
}

function WaveformBars({ active }: { active: boolean }) {
  return (
    <>
      {WAVEFORM_HEIGHTS.map((height, index) => (
        <div
          key={index}
          className={[
            'flex-1 rounded-full',
            active ? 'bg-blue-500 dark:bg-[var(--accent)]' : 'bg-slate-200 dark:bg-[var(--bg-tertiary)]',
          ].join(' ')}
          style={{ height: `${height}px` }}
        />
      ))}
    </>
  )
}

function IconButton({
  children,
  label,
  onClick,
  disabled,
}: {
  children: React.ReactNode
  label: string
  onClick: () => void
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className="flex h-10 w-10 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 disabled:cursor-not-allowed disabled:opacity-50 dark:text-[var(--text-tertiary)] dark:hover:bg-[var(--bg-tertiary)] dark:hover:text-[var(--text-secondary)]"
    >
      {children}
    </button>
  )
}

function PlayIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M8 5.14v13.72a1 1 0 0 0 1.5.86l11.04-6.86a1 1 0 0 0 0-1.72L9.5 4.28A1 1 0 0 0 8 5.14Z" />
    </svg>
  )
}

function PauseIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M7 5.5A1.5 1.5 0 0 1 8.5 4h1A1.5 1.5 0 0 1 11 5.5v13A1.5 1.5 0 0 1 9.5 20h-1A1.5 1.5 0 0 1 7 18.5v-13ZM13 5.5A1.5 1.5 0 0 1 14.5 4h1A1.5 1.5 0 0 1 17 5.5v13A1.5 1.5 0 0 1 15.5 20h-1A1.5 1.5 0 0 1 13 18.5v-13Z" />
    </svg>
  )
}

function SkipBackIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M11 5.5v13L5 12l6-6.5ZM18 5.5v13l-6-6.5 6-6.5Z" />
    </svg>
  )
}

function SkipForwardIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M13 5.5v13l6-6.5-6-6.5ZM6 5.5v13l6-6.5-6-6.5Z" />
    </svg>
  )
}
